import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

// bump key because shape changed (was array, now object)
const STORAGE_KEY = "borrowCart_v2";

function safeParse(json, fallback) {
  try {
    const v = JSON.parse(json);
    return v ?? fallback;
  } catch {
    return fallback;
  }
}

const DEFAULT_HEADER = {
  event_name: "",
  event_pic_name: "",
  event_date: "", // date only (YYYY-MM-DD)
};

const BorrowCartContext = createContext(null);

export function BorrowCartProvider({ children }) {
  const [state, setState] = useState(() => {
    const raw = localStorage.getItem(STORAGE_KEY);

    // Backward compatibility: old storage was an array of items
    if (!raw) return { items: [], header: { ...DEFAULT_HEADER } };

    const parsed = safeParse(raw, null);

    if (Array.isArray(parsed)) {
      return { items: parsed, header: { ...DEFAULT_HEADER } };
    }

    return {
      items: Array.isArray(parsed?.items) ? parsed.items : [],
      header: parsed?.header ? { ...DEFAULT_HEADER, ...parsed.header } : { ...DEFAULT_HEADER },
    };
  });

  const items = state.items;
  const header = state.header;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const api = useMemo(() => {
    const addItem = (item, quantity = 1) => {
      const qty = Number(quantity) || 0;
      if (qty <= 0) return;

      setState((prev) => {
        const prevItems = prev.items || [];
        const idx = prevItems.findIndex((x) => x.itemId === item.itemId);

        if (idx >= 0) {
          const copy = [...prevItems];
          const prevQty = Number(copy[idx].quantity) || 0;

          copy[idx] = {
            ...copy[idx],
            code: item.code ?? copy[idx].code ?? "",
            name: item.name ?? copy[idx].name ?? "",
            unit: item.unit ?? copy[idx].unit ?? "",
            image: item.image ?? item.image_url ?? copy[idx].image ?? copy[idx].image_url ?? null,
            image_url: item.image_url ?? copy[idx].image_url ?? null,
            available:
              item.available !== undefined && item.available !== null
                ? item.available
                : copy[idx].available,
            quantity: prevQty + qty,
          };

          return { ...prev, items: copy };
        }

        return {
          ...prev,
          items: [
            ...prevItems,
            {
              itemId: item.itemId,
              code: item.code || "",
              name: item.name || "",
              unit: item.unit || "",
              image: item.image || item.image_url || null,
              image_url: item.image_url || null,
              available: item.available ?? null,
              quantity: qty,
            },
          ],
        };
      });
    };

    const removeItem = (itemId) => {
      setState((prev) => ({ ...prev, items: (prev.items || []).filter((x) => x.itemId !== itemId) }));
    };

    const setQuantity = (itemId, quantity) => {
      if (quantity === "") {
        setState((prev) => ({
          ...prev,
          items: (prev.items || []).map((x) => (x.itemId === itemId ? { ...x, quantity: "" } : x)),
        }));
        return;
      }

      const qty = Number(quantity);
      setState((prev) => ({
        ...prev,
        items: (prev.items || []).map((x) =>
          x.itemId === itemId ? { ...x, quantity: Number.isFinite(qty) ? qty : "" } : x
        ),
      }));
    };

    const setHeaderField = (field, value) => {
      setState((prev) => ({
        ...prev,
        header: { ...(prev.header || DEFAULT_HEADER), [field]: value },
      }));
    };

    const setHeaderBulk = (patch) => {
      setState((prev) => ({
        ...prev,
        header: { ...(prev.header || DEFAULT_HEADER), ...(patch || {}) },
      }));
    };

    // clear cart + header (prevents old event data leaking into next borrow)
    const clear = () => setState({ items: [], header: { ...DEFAULT_HEADER } });

    const totalCount = (items || []).reduce((sum, x) => sum + (Number(x.quantity) || 0), 0);

    return {
      items,
      header,
      addItem,
      removeItem,
      setQuantity,
      setHeaderField,
      setHeaderBulk,
      clear,
      totalCount,
    };
  }, [items, header]);

  return <BorrowCartContext.Provider value={api}>{children}</BorrowCartContext.Provider>;
}

export function useBorrowCart() {
  const ctx = useContext(BorrowCartContext);
  if (!ctx) throw new Error("useBorrowCart must be used inside BorrowCartProvider");
  return ctx;
}